import { create } from 'zustand';
import {
  PayrollCase,
  DashboardStats,
  CaseFilters,
  ConfidenceMetadata,
  ConfidenceError,
  ConfidenceErrorState,
  createConfidenceError,
  isErrorRecoverable,
  ERROR_CODES,
} from '../types';
import {
  initializeConfidenceMetadata,
  enhancePayrollCaseWithMetadata,
} from '../utils/confidenceMetadataUtils';
import {
  evaluateCase,
  evaluateCaseWithErrorHandling,
  flagCaseWithErrorHandling,
  createErrorState,
  retryWithBackoff,
} from '../utils/confidenceUtils';
import apiService from '../services/api';
import logger from '../utils/logger';

interface StoreState {
  cases: PayrollCase[];
  stats: DashboardStats;
  selectedCase: PayrollCase | null;
  isLoading: boolean;
  currentUser: string | null; // Current user name for filtering

  // Confidence tracking state (Task #10)
  confidenceMetrics: {
    totalCases: number;
    averageConfidence: number;
    distributionByLevel: Record<string, number>;
    flaggedCases: number;
    processingErrors: number;
  };

  // Task #16: Error state management
  errorState: ConfidenceErrorState;
  isRecoveringFromErrors: boolean;

  // Actions
  setCases: (cases: PayrollCase[]) => void;
  setStats: (stats: DashboardStats) => void;
  setSelectedCase: (caseItem: PayrollCase | null) => void;
  setCurrentUser: (userName: string | null) => void;
  updateCase: (caseNumber: string, updates: Partial<PayrollCase>) => void;
  setLoading: (loading: boolean) => void;

  // Filtering actions
  fetchCases: (filters?: CaseFilters) => Promise<void>;
  fetchMyCases: (userName: string) => Promise<void>;

  // Confidence tracking actions (Task #10)
  updateCaseConfidenceMetadata: (
    caseNumber: string,
    metadata: Partial<ConfidenceMetadata>
  ) => void;
  trackConfidenceHistory: (
    caseNumber: string,
    confidence: number,
    trigger: string,
    reason?: string
  ) => void;
  calculateConfidenceMetrics: () => void;

  // Enhanced confidence filtering actions (Task #11)
  fetchCasesByWorkflow: (workflow: string) => Promise<void>;
  fetchCasesByConfidenceLevel: (level: 'high' | 'medium' | 'low') => Promise<void>;
  refreshCaseEvaluations: () => void;

  // Task #16: Error management actions
  addConfidenceError: (error: ConfidenceError) => void;
  removeConfidenceError: (errorId: string) => void;
  clearErrorsForCase: (caseNumber: string) => void;
  clearAllErrors: () => void;
  retryFailedOperation: (errorId: string) => Promise<boolean>;
  recoverFromErrors: () => Promise<void>;
  getErrorsForCase: (caseNumber: string) => ConfidenceError[];

  // Async actions
  fetchStats: () => Promise<void>;
  fetchCase: (caseNumber: string) => Promise<void>;
  fetchCaseDetails: (caseNumber: string) => Promise<{
    payrollEntries: any[];
    documents: any[];
    emails: any[];
  } | null>;
}

const loadCases = async (currentUser?: string): Promise<PayrollCase[]> => {
  try {
    const isConnected = await apiService.testConnection();
    if (!isConnected) {
      logger.warn('[Store] Database connection failed');
      return [];
    }

    const cases = await apiService.getCases();
    logger.debug(`[Store] Loaded ${cases.length} cases from database`);

    return cases.map((caseItem) => enhancePayrollCaseWithMetadata(caseItem));
  } catch (error) {
    logger.error('[Store] Failed to load cases from database', error);
    return [];
  }
};

export const useStore = create<StoreState>((set, get) => ({
  cases: [],
  stats: {
    totalCases: 0,
    openCases: 0,
    resolvedCases: 0,
    averageResolutionTime: '0 mins',
  },
  selectedCase: null,
  isLoading: false,
  currentUser: null,

  // Initialize confidence tracking state (Task #10)
  confidenceMetrics: {
    totalCases: 0,
    averageConfidence: 0,
    distributionByLevel: {},
    flaggedCases: 0,
    processingErrors: 0,
  },

  // Task #16: Initialize error state
  errorState: createErrorState(),
  isRecoveringFromErrors: false,

  setCases: (cases) => set({ cases }),
  setStats: (stats) => set({ stats }),
  setSelectedCase: (selectedCase) => set({ selectedCase }),
  setCurrentUser: (currentUser) => set({ currentUser }),
  setLoading: (isLoading) => set({ isLoading }),

  // Confidence tracking actions implementation (Task #10)
  updateCaseConfidenceMetadata: (caseNumber, metadata) => {
    const cases = get().cases.map((caseItem) => {
      if (caseItem.caseNumber === caseNumber) {
        const existingMetadata =
          caseItem.confidenceMetadata || initializeConfidenceMetadata(caseItem);
        return {
          ...caseItem,
          confidenceMetadata: {
            ...existingMetadata,
            ...metadata,
          },
        };
      }
      return caseItem;
    });
    set({ cases });
    get().calculateConfidenceMetrics();
  },

  trackConfidenceHistory: (caseNumber, confidence, trigger, reason) => {
    const timestamp = new Date().toISOString();
    const historyEntry = {
      timestamp,
      confidence,
      level:
        confidence >= 90
          ? ('high' as const)
          : confidence >= 75
            ? ('medium' as const)
            : ('low' as const),
      trigger: trigger as
        | 'initial'
        | 'reprocessing'
        | 'manual_adjustment'
        | 'business_rule_penalty',
      adjustmentReason: reason,
    };

    get().updateCaseConfidenceMetadata(caseNumber, {
      confidenceHistory: [
        ...(get().cases.find((c) => c.caseNumber === caseNumber)?.confidenceMetadata
          ?.confidenceHistory || []),
        historyEntry,
      ],
      lastEvaluationTimestamp: timestamp,
      evaluationCount:
        (get().cases.find((c) => c.caseNumber === caseNumber)?.confidenceMetadata
          ?.evaluationCount || 0) + 1,
    });
  },

  calculateConfidenceMetrics: () => {
    const cases = get().cases;
    const totalCases = cases.length;

    if (totalCases === 0) {
      set({
        confidenceMetrics: {
          totalCases: 0,
          averageConfidence: 0,
          distributionByLevel: {},
          flaggedCases: 0,
          processingErrors: 0,
        },
      });
      return;
    }

    const averageConfidence =
      cases.reduce((sum, c) => sum + c.aiConfidence, 0) / totalCases;

    const distributionByLevel = cases.reduce(
      (acc, c) => {
        const level =
          c.confidenceMetadata?.confidenceLevel ||
          (c.aiConfidence >= 90 ? 'high' : c.aiConfidence >= 75 ? 'medium' : 'low');
        acc[level] = (acc[level] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    const flaggedCases = cases.filter(
      (c) => c.routingInfo?.requiresFlagging || c.aiConfidence < 85
    ).length;

    const processingErrors = cases.reduce(
      (sum, c) => sum + (c.processingMetadata?.processingErrors?.length || 0),
      0
    );

    set({
      confidenceMetrics: {
        totalCases,
        averageConfidence: Math.round(averageConfidence * 100) / 100,
        distributionByLevel,
        flaggedCases,
        processingErrors,
      },
    });
  },

  // Enhanced confidence filtering actions implementation (Task #11)
  fetchCasesByWorkflow: async (workflow: string) => {
    set({ isLoading: true });
    try {
      // Get all cases and filter by workflow
      const allCases = get().cases;

      const workflowCases = allCases.filter((caseItem) => {
        const evaluation = evaluateCase(caseItem);
        return evaluation.workflow === workflow && evaluation.isVisible;
      });

      set({ cases: workflowCases });
      get().calculateConfidenceMetrics();
    } catch (error) {
      logger.error('[Store] Failed to fetch cases by workflow', error);
    } finally {
      set({ isLoading: false });
    }
  },

  fetchCasesByConfidenceLevel: async (level: 'high' | 'medium' | 'low') => {
    set({ isLoading: true });
    try {
      // Get all cases and filter by confidence level
      const allCases = get().cases;

      const levelCases = allCases.filter((caseItem) => {
        const evaluation = evaluateCase(caseItem);
        return evaluation.simple.level === level && evaluation.isVisible;
      });

      set({ cases: levelCases });
      get().calculateConfidenceMetrics();
    } catch (error) {
      logger.error('[Store] Failed to fetch cases by confidence level', error);
    } finally {
      set({ isLoading: false });
    }
  },

  refreshCaseEvaluations: () => {
    const cases = get().cases;
    const refreshedCases = cases.map((caseItem) => {
      const enhancedCase = enhancePayrollCaseWithMetadata(caseItem);
      const evaluation = evaluateCase(enhancedCase);

      return {
        ...enhancedCase,
        routingInfo: {
          ...enhancedCase.routingInfo,
          assignedWorkflow: evaluation.workflow as
            | 'AUTO_PROCESS'
            | 'HIGH_CONFIDENCE'
            | 'MEDIUM_CONFIDENCE'
            | 'LOW_CONFIDENCE'
            | 'MANUAL_ESCALATION',
          estimatedProcessingTime: evaluation.estimatedTime,
          requiresCSRReview: evaluation.requiresCSR,
          requiresFlagging: evaluation.requiresFlag,
          flaggingReason: evaluation.flagReason,
          workflowStatus: enhancedCase.routingInfo?.workflowStatus || 'pending',
          routingTimestamp: new Date().toISOString(),
        },
      };
    });

    set({ cases: refreshedCases });
    get().calculateConfidenceMetrics();
  },

  updateCase: (caseNumber, updates) => {
    const cases = get().cases.map((caseItem) =>
      caseItem.caseNumber === caseNumber
        ? { ...caseItem, ...updates, lastUpdated: new Date().toISOString() }
        : caseItem
    );
    set({ cases });

    // Update selected case if it's the one being updated
    const selectedCase = get().selectedCase;
    if (selectedCase && selectedCase.caseNumber === caseNumber) {
      set({
        selectedCase: {
          ...selectedCase,
          ...updates,
          lastUpdated: new Date().toISOString(),
        },
      });
    }

    // Persist to localStorage
    localStorage.setItem('payroll-cases', JSON.stringify(cases));
  },

  fetchCases: async (filters?: CaseFilters) => {
    set({ isLoading: true });
    try {
      const baseCases = await loadCases(get().currentUser || undefined);

      // Task #16: Enhanced confidence filtering with error handling
      const enhancedCases = await Promise.all(
        baseCases.map(async (caseItem) => {
          // Enhance case with confidence metadata if not already present
          const enhancedCase = enhancePayrollCaseWithMetadata(caseItem);

          // Use error-aware evaluation
          const { evaluation, error, recovered } =
            await evaluateCaseWithErrorHandling(enhancedCase);

          // Track any errors that occurred
          if (error) {
            get().addConfidenceError(error);
          }

          // Add evaluation results to the case for UI use
          return {
            ...enhancedCase,
            // Store evaluation results in routingInfo for easy access
            routingInfo: {
              ...enhancedCase.routingInfo,
              assignedWorkflow: evaluation.workflow as
                | 'AUTO_PROCESS'
                | 'HIGH_CONFIDENCE'
                | 'MEDIUM_CONFIDENCE'
                | 'LOW_CONFIDENCE'
                | 'MANUAL_ESCALATION',
              estimatedProcessingTime: evaluation.estimatedTime,
              requiresCSRReview: evaluation.requiresCSR,
              requiresFlagging: evaluation.requiresFlag,
              flaggingReason: evaluation.flagReason,
              workflowStatus: 'pending' as const,
              routingTimestamp: new Date().toISOString(),
            },
            // Task #16: Add error state information
            hasConfidenceErrors: !!error,
            isUsingFallbackEvaluation: recovered,
          };
        })
      );

      // Apply visibility filtering (cases below 85% confidence are hidden)
      logger.debug('[Store] Applying confidence filtering');

      let filteredCases = enhancedCases.filter((caseItem) => {
        const evaluation = evaluateCase(caseItem);
        return evaluation.isVisible;
      });

      logger.debug('[Store] Visible cases after confidence filter');

      // Apply additional filters if provided
      if (filters) {
        // COMMENTED OUT: Filter to show only cases assigned to current user
        // This allows all users to see all cases regardless of assignment
        /* if (filters.assignedToCurrentUser) {
          const currentUser = get().currentUser;
          filteredCases = filteredCases.filter(caseItem => caseItem.assignedTo === currentUser);
        } */

        if (filters.status) {
          filteredCases = filteredCases.filter(
            (caseItem) => caseItem.status === filters.status
          );
        }

        if (filters.minConfidence !== undefined) {
          filteredCases = filteredCases.filter(
            (caseItem) => caseItem.aiConfidence >= filters.minConfidence!
          );
        }

        // Enhanced confidence-based filtering (Task #11)
        if (filters.confidenceLevel) {
          filteredCases = filteredCases.filter((caseItem) => {
            const evaluation = evaluateCase(caseItem);
            return evaluation.level === filters.confidenceLevel;
          });
        }
      }

      set({ cases: filteredCases });
      // Calculate confidence metrics after successful loading
      get().calculateConfidenceMetrics();
    } catch (error: any) {
      logger.error('[Store] Failed to fetch cases', error);

      // Task #16: Create and track system error
      const systemError = createConfidenceError(
        'confidence_calculation',
        ERROR_CODES.API_UNAVAILABLE,
        `Failed to load cases: ${error.message}`,
        {
          severity: 'critical',
          context: {
            operation: 'fetch_cases',
            stackTrace: error.stack,
            userAction: 'loading_dashboard',
          },
        }
      );
      get().addConfidenceError(systemError);

      set({ cases: [] });
    } finally {
      set({ isLoading: false });
      // Calculate confidence metrics after loading cases
      get().calculateConfidenceMetrics();
    }
  },

  fetchMyCases: async (userName: string) => {
    logger.debug('[Store] fetchMyCases called');
    set({ currentUser: userName });
    // COMMENTED OUT: No longer filtering by assignedToCurrentUser - show all cases
    await get().fetchCases(/* { assignedToCurrentUser: true } */);
  },

  fetchStats: async () => {
    try {
      const stats = await apiService.getDashboardStats();
      set({ stats });
    } catch (error) {
      logger.error('[Store] Failed to fetch stats', error);
    }
  },

  fetchCase: async (caseNumber) => {
    set({ isLoading: true });
    try {
      let caseItem: PayrollCase | null = null;

      caseItem = await apiService.getCase(caseNumber);
      if (caseItem) {
        caseItem = enhancePayrollCaseWithMetadata(caseItem);
      }

      if (!caseItem) {
        const currentCases = get().cases;
        caseItem = currentCases.find((c) => c.caseNumber === caseNumber) || null;

        if (caseItem) {
          logger.debug('[Store] Loaded case from current state');
        }
      }

      set({ selectedCase: caseItem });
    } catch (error) {
      logger.error('[Store] Failed to fetch case', error);
      set({ selectedCase: null });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchCaseDetails: async (caseNumber: string) => {
    try {
      const details = await apiService.getCaseDetails(caseNumber);
      if (details) {
        return {
          payrollEntries: details.payrollEntries,
          documents: details.documents,
          emails: details.emails,
        };
      }
      return null;
    } catch (error) {
      logger.error('[Store] Failed to fetch case details', error);
      return null;
    }
  },

  // Task #16: Error management action implementations
  addConfidenceError: (error: ConfidenceError) => {
    const currentErrors = get().errorState.errors;
    const updatedErrors = [...currentErrors, error];
    const newErrorState = createErrorState(updatedErrors);

    set({ errorState: newErrorState });

    logger.error('[Store] Confidence error added', error.errorType, error.errorMessage);
  },

  removeConfidenceError: (errorId: string) => {
    const currentErrors = get().errorState.errors;
    const updatedErrors = currentErrors.filter((e) => e.id !== errorId);
    const newErrorState = createErrorState(updatedErrors);

    set({ errorState: newErrorState });
  },

  clearErrorsForCase: (caseNumber: string) => {
    const currentErrors = get().errorState.errors;
    const updatedErrors = currentErrors.filter((e) => e.caseNumber !== caseNumber);
    const newErrorState = createErrorState(updatedErrors);

    set({ errorState: newErrorState });
  },

  clearAllErrors: () => {
    set({ errorState: createErrorState() });
  },

  getErrorsForCase: (caseNumber: string): ConfidenceError[] => {
    return get().errorState.errorsByCase[caseNumber] || [];
  },

  retryFailedOperation: async (errorId: string): Promise<boolean> => {
    const error = get().errorState.errors.find((e) => e.id === errorId);
    if (!error || !isErrorRecoverable(error)) {
      return false;
    }

    set({ isRecoveringFromErrors: true });

    try {
      // Increment retry count
      error.retryCount++;

      switch (error.errorType) {
        case 'flagging_failed':
          if (error.caseNumber) {
            const { success } = await flagCaseWithErrorHandling(
              error.caseNumber,
              error.context.parameters?.reason || 'Retry flagging operation'
            );
            if (success) {
              get().removeConfidenceError(errorId);
              return true;
            }
          }
          break;

        case 'confidence_calculation':
          if (error.caseNumber) {
            const caseItem = get().cases.find((c) => c.caseNumber === error.caseNumber);
            if (caseItem) {
              const { evaluation, error: newError } =
                await evaluateCaseWithErrorHandling(caseItem);
              if (!newError) {
                get().removeConfidenceError(errorId);
                // Re-fetch cases to update with new evaluation
                await get().fetchCases();
                return true;
              }
            }
          }
          break;

        default:
          logger.warn('[Store] Retry not implemented for error type:', error.errorType);
          break;
      }

      return false;
    } catch (retryError) {
      logger.error('[Store] Retry operation failed', retryError);
      return false;
    } finally {
      set({ isRecoveringFromErrors: false });
    }
  },

  recoverFromErrors: async () => {
    const { errorState } = get();
    if (!errorState.hasErrors) return;

    set({ isRecoveringFromErrors: true });

    try {
      const recoverableErrors = errorState.errors.filter(isErrorRecoverable);

      // Attempt to recover from each error
      const recoveryPromises = recoverableErrors.map(async (error) => {
        try {
          return await get().retryFailedOperation(error.id);
        } catch (err) {
          logger.error('[Store] Recovery failed for error', err);
          return false;
        }
      });

      const results = await Promise.all(recoveryPromises);
      const successCount = results.filter(Boolean).length;

      logger.debug('[Store] Error recovery completed');

      // Re-fetch cases to ensure consistency after recovery
      if (successCount > 0) {
        await get().fetchCases();
      }
    } catch (error) {
      logger.error('[Store] Error recovery process failed', error);
    } finally {
      set({ isRecoveringFromErrors: false });
    }
  },
}));
